import { createFileRoute } from "@tanstack/react-router";
import { fetchStrapiAPI } from "@/services/strapi";
import Navbar from "@/components/Home/NavBar";
import Footer from "@/components/Home/Footer";
import ScrollAnimation from "@/components/Home/Animations";
import Hero from "@/components/Home/Hero";
import Features from "@/components/Home/Features";
import HowItWorks from "@/components/Home/HowItWorks";
import Testimonials from "@/components/Home/Testimonial";
import Pricing from "@/components/Home/Pricing";
import FAQ from "@/components/Home/FAQ";

// Types for Strapi response
interface HomepageData {
  id: number;
  documentId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  sections: Array<{
    __component: string;
    id: number;
    [key: string]: any;
  }>;
}

export const Route = createFileRoute("/")({
  component: HomePage,
  staleTime: 5 * 1000 * 60,
  gcTime: 10 * 1000 * 60,
  preload: true,
  preloadStaleTime: 10 * 1000 * 60,
  loader: async () => {
    try {
      const homepageResponse = await fetchStrapiAPI("/homepage?pLevel");
      return { homepageData: homepageResponse.data as HomepageData };
    } catch (error) {
      console.error("Error fetching homepage data:", error);
      return { homepageData: null };
    }
  },
});

function HomePage() {
  const { homepageData } = Route.useLoaderData();

  if (!homepageData) {
    return <div>Error loading homepage data</div>;
  }

  // Helper function to find section by component name
  const findSection = (componentName: string) => {
    if (!homepageData.sections) {
      return null;
    }
    return homepageData.sections.find(
      (section) => section.__component === `homepage.${componentName}`,
    );
  };

  // Get each section's data
  const heroSection = findSection("hero-section");
  const featuresSection = findSection("features-section");
  const howItWorksSection = findSection("how-it-works-section");
  const testimonialsSection = findSection("testimonials-section");
  const pricingSection = findSection("pricing-section");
  const faqSection = findSection("faq-section");
  return (
    <ScrollAnimation>
      <Navbar />
      <main>
        {heroSection && (
          <Hero
            headline={heroSection.headline}
            subheadline={heroSection.subheadline}
            tagline={heroSection.tagline}
            ctaButtonPrimary={heroSection.ctaButtonPrimary}
            ctaButtonSecondary={heroSection.ctaButtonSecondary}
            heroImage={heroSection.heroImage}
          />
        )}

        {featuresSection && (
          <Features
            title={featuresSection.title}
            description={featuresSection.description}
            tagline={featuresSection.tagline}
            featureItems={featuresSection.featureItems}
          />
        )}

        {howItWorksSection && (
          <HowItWorks
            title={howItWorksSection.title}
            description={howItWorksSection.description}
            tagline={howItWorksSection.tagline}
            steps={howItWorksSection.steps}
          />
        )}

        {testimonialsSection && (
          <Testimonials
            title={testimonialsSection.title}
            description={testimonialsSection.description}
            tagline={testimonialsSection.tagline}
            testimonials={testimonialsSection.testimonials}
          />
        )}

        {pricingSection && (
          <Pricing
            title={pricingSection.title}
            description={pricingSection.description}
            tagline={pricingSection.tagline}
            pricingPlans={pricingSection.pricingPlans}
          />
        )}

        {faqSection && (
          <FAQ
            title={faqSection.title}
            description={faqSection.description}
            tagline={faqSection.tagline}
            faqItems={faqSection.faqItems}
          />
        )}
      </main>
      <Footer />
    </ScrollAnimation>
  );
}
